import Vue from 'vue';
import api from '@/services/api';
import { make } from 'vuex-pathify';

const state = {
  scadaMeasurements: {},
  scadaPointsRelations: [],
  selectedDataSource: {},
  scadaThreeMeasurements: [],
  scadaThreeMeasurementsPoint: [],
};

const mutations = {
  ...make.mutations(state),
  SET_SCADA_MEASUREMENTS: (state, data) => {
    const { measurements, id } = data;
    Vue.set(state.scadaMeasurements, id, measurements);
  },
};

const actions = {
  async getScadaPointMeasurements({ commit }, id) {
    const r = await api.get(`scada/measurements/${id}`);
    commit('SET_SCADA_MEASUREMENTS', { measurements: r.data.data, id });
    return r.data.data;
  },
  async postScadaPointMeasurements({ dispatch }, data) {
    const { scadaPointId, payload } = data;
    await api.post('scada/measurements', payload);
    await dispatch('getScadaPointMeasurements', scadaPointId);
  },
  async putScadaPointMeasurement({ dispatch }, data) {
    const { scadaPointId, measurementId, payload } = data;
    await api.put(`scada/measurements/${measurementId}`, payload);
    await dispatch('getScadaPointMeasurements', scadaPointId);
  },
  async deleteScadaPointMeasurement({ dispatch }, data) {
    const { scadaPointId, measurementId } = data;
    await api.delete(`scada/measurements/${measurementId}`);
    await dispatch('getScadaPointMeasurements', scadaPointId);
  },
  async assignMeasurementToScadaPoint({ dispatch }, data) {
    const { scadaPointId, payload } = data;
    await api.post('scada/measure_attributes', payload);
    await dispatch('updateMeasurementsAttributesValues');
    await dispatch('getScadaPointMeasurements', scadaPointId);
  },
  async unassignMeasurementToScadaPoint({ dispatch }, data) {
    const { scadaPointId, relation } = data;
    await api.delete(`scada/measure_attributes/${relation}`);
    await dispatch('updateMeasurementsAttributesValues');
    await dispatch('getScadaPointMeasurements', scadaPointId);
  },
  async editSelectedDataSource({ dispatch }, payload) {
    await api.put('scada/selected_datasource', payload);
    await dispatch('getSelectedDataSource');
  },
  async getSelectedDataSource({ commit }) {
    const r = await api.get('scada/selected_datasource');
    commit('SET_SELECTED_DATA_SOURCE', r.data.data);
  },
  async updateMeasurementsAttributesValues() {
    await api.get('scada/service/measurements/update');
  },
  async getScadaMeasurementSeriesData(store, data) {
    const { measurementId, payload } = data;
    const r = await api.get(`scada/service/measurements/${measurementId}`, { params: payload });
    return r.data.data;
  },
  async addScadaPointsRelations({ dispatch }, payload) {
    await api.post('water/scada_relations', payload);
    await dispatch('getScadaPointsRelations');
  },
  async editScadaPointRelation({ dispatch }, data) {
    const { payload, id } = data;
    await api.put(`water/scada_relations/${id}`, payload);
    await dispatch('getScadaPointsRelations');
  },
  async deleteScadaPointRelation({ dispatch }, id) {
    await api.delete(`water/scada_relations/${id}`);
    await dispatch('getScadaPointsRelations');
  },
  async deleteScadaPointRelations({ dispatch }, id) {
    await api.delete(`water/scada_relations/scada_point/${id}`);
    await dispatch('getScadaPointsRelations');
  },
  async getScadaPointsRelations({ commit }) {
    const r = await api.get('water/scada_relations');
    commit('SET_SCADA_POINTS_RELATIONS', r.data.data);
  },
  async getScadaMeasurementParameters() {
    const r = await api.get('/scada/measure_parameters');
    return r.data.data;
  },
  async getScadaThreeAttributes() {
    const r = await api.get('/scada3/relations');
    return r.data.data;
  },
  async addScadaThreeAttribute(store, id) {
    await api.post(`/scada3/relations/${id}/attach`);
  },
  async deleteScadaThreeAttribute(store, id) {
    await api.post(`/scada3/relations/${id}/detach`);
  },
  async getScadaThreeMeasurementParameters() {
    const r = await api.get('/scada3/measure_parameters');
    return r.data.data;
  },
  async getScadaThreeSystems() {
    const r = await api.get(`scada3/service/available_systems`);
    return r.data.data;
  },
  async getScadaThreeMeasurements({ commit }, dataSource) {
    const r = await api.post(`/dataio/data_sources/${dataSource}/read`, { features_filter: {} });
    const measurements = r.data.data.features.features.map(feature => {
      return {
        ...feature.properties,
        id: feature.id,
      };
    });
    commit('SET_SCADA_THREE_MEASUREMENTS', measurements);
  },
  async getScadaThreeMeasurementsPoint({ commit }, id) {
    const r = await api.get(`scada3/measurements/${id}`);
    commit('SET_SCADA_THREE_MEASUREMENTS_POINT', r.data.data);
  },
  async editScadaThreeMeasurementPoint({ dispatch }, { datasource, id, body, scadaPointId }) {
    await api.put(`/dataio/data_sources/${datasource}/features/${id}`, { feature: body });
    await dispatch('getScadaThreeMeasurementsPoint', scadaPointId);
  },
  async updateScadaThreeMeasurements() {
    await api.get('scada3/measurements/update');
  },
  async assignScadaThreeRelation({ dispatch }, data) {
    const { scadaPointId, payload } = data;
    await api.post('scada3/measure_attributes', payload);
    await dispatch('updateScadaThreeMeasurements');
    await dispatch('getScadaThreeMeasurementsPoint', scadaPointId);
  },
  async unassignScadaThreeRelation({ dispatch }, data) {
    const { scadaPointId, relation } = data;
    await api.delete(`scada3/measure_attributes/${relation}`);
    await dispatch('updateScadaThreeMeasurements');
    await dispatch('getScadaThreeMeasurementsPoint', scadaPointId);
  },
  async getScadaThreeMeasurementSeriesData(store, data) {
    const { measurementId, payload } = data;
    const r = await api.get(`scada3/service/series/${measurementId}`, { params: payload });
    return r.data.data;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};
